// ModalContext.js
import React, { createContext, useContext, useState } from 'react';
import icon from '../../assets/icons/step-white.svg'
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import TimeSelectionModal from './TimeModal';
const ModalContext = createContext();

export function useModal() {
  return useContext(ModalContext);
}

export function ModalProvider({ children }) {
    const [isModalOpen, setModalOpen] = useState(false);

    const openModal = () => setModalOpen(true);
    const closeModal = () => setModalOpen(false);
    
    return (
        <ModalContext.Provider value={{ isModalOpen, openModal, closeModal }}>
        {children}
        {isModalOpen && <ModalComponent onClose={closeModal} />}
        </ModalContext.Provider>
    );
}

function ModalComponent({ onClose }) {
    const { t } = useTranslation();
    // step state: 1 = contact info, 2 = time selection
    const [step, setStep] = useState(1);
    const { register, handleSubmit, setValue, formState: { errors }, watch, reset, trigger } = useForm({
      defaultValues: {
        name: '',
        company: '',
        phone: '',
        message: '',
        service: 'Не указано',
        time: '',
      },
    });
    const formState = watch();
    const [loading, setLoading] = useState(false);

    // Submit final form data to API
    const submit = async (data) => {
      setLoading(true);
      try {
        const request = await fetch('https://mail-server-xi.vercel.app/mail', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(data)
        });
        // Push dataLayer event if needed
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: 'formSubmission',
          formName: 'Contact us',
          formData: data
        });
  
        const res = await request.json();
        alert(res?.message);
        reset();
        onClose(); // Close the modal after successful submission
      } catch (error) {
        console.error("Error submitting form:", error);
        alert('Ошибка отправки формы');
      }
      setLoading(false);
    };
  
    // Phone formatting function remains unchanged
    const formatPhoneNumber = (value) => {
      const numericValue = value.replace(/\D/g, '');
      let formattedValue = '+';
      if (numericValue.length >= 1) {
        formattedValue += numericValue.substring(0, 1);
      }
      if (numericValue.length > 1) {
        formattedValue += ' (' + numericValue.substring(1, 4);
      }
      if (numericValue.length > 4) {
        formattedValue += ') ' + numericValue.substring(4, 7);
      }
      if (numericValue.length > 7) {
        formattedValue += ' ' + numericValue.substring(7, 9);
      }
      if (numericValue.length > 9) {
        formattedValue += ' ' + numericValue.substring(9);
      }
      return formattedValue.substring(0, 18);
    };
  
    const handlePhoneNumberChange = (event) => {
      const inputPhoneNumber = event.target.value;
      if (inputPhoneNumber.length < 1 || !inputPhoneNumber.startsWith('+')) {
        event.target.value = '+' + inputPhoneNumber.replace(/^\+/, '');
      } else {
        event.target.value = formatPhoneNumber(inputPhoneNumber);
      }
    };
  
    // Validate step1 fields and then proceed to step2
    const proceedToTimeSelection = async () => {
      // Validate only required fields in step 1: name and phone
      const valid = await trigger(['name', 'phone', 'company', 'message']);
      if (valid) {
        setStep(2);
      }
    };
  
    // Render first step: contact information
    const renderStep1 = () => (
      <>
        <h2 className="text-2xl font-semibold font-title">
          Оставьте номер телефона и мы вам позвоним
        </h2>
        <div className="flex flex-col space-y-1">
          <input
            {...register('name', { required: true })}
            type="text"
            className="bg-white border border-black rounded-full px-4 py-2 outline-none focus:ring-0 focus:border-[#FF4548]"
            placeholder={t('dialog-form.name-placeholder')}
          />
          {errors.name && <span className="text-red-500">Введите ваше имя</span>}
        </div>
        <div className="flex flex-col space-y-1">
          <input
            {...register('company', {required: true})}
            type="text"
            className="bg-white border border-black rounded-full px-4 py-2 outline-none focus:ring-0 focus:border-[#FF4548]"
            placeholder={t('dialog-form.company-placeholder')}
          />
          {errors.company && <span className="text-red-500">Введите название компании</span>}
        </div>
        <div className="flex flex-col space-y-1">
          <input
            type="tel"
            id="phone"
            placeholder="+7"
            className="bg-white border border-black rounded-full px-4 py-2 text-lg outline-none focus:ring-0 focus:border-[#FF4548]"
            {...register('phone', {
              required: true,
              minLength: 18,
              maxLength: 18
            })}
            onChange={handlePhoneNumberChange}
          />
          {errors.phone && <span className="text-red-500">Номер телефона не действительный</span>}
        </div>
        <div className="flex flex-col space-y-1">
          <textarea
            {...register('message', { required: true, maxLength: 100 })}
            rows={3}
            className="bg-white border border-black rounded-xl px-4 py-2 text-lg outline-none focus:ring-0 focus:border-[#FF4548]"
            placeholder="Опишите вашу задачу"
          ></textarea>
          {errors.message && <span className="text-red-500">Введите вашу задачу</span>}
        </div>
        <button
        type='button'
        onClick={proceedToTimeSelection}
        className="px-8 py-3 bg-[#FF4548] text-white rounded-full hover:bg-black hover:border-[#FF4548] border border-black transition-all duration-300 ease-in-out"
      >
          {t('navbar.order')}
      </button>
      </>
    );
  
    // Render second step: time selection
    const renderStep2 = () => (
      <>
        <h2 className="text-2xl font-semibold font-title">
          Выберите удобное время для звонка
        </h2>
        <div className="flex flex-col space-y-3 mb-6">
          <label className="flex items-center space-x-2 text-xl">
            <input
              type="radio"
              className='w-6 h-6'
              value="В ближайшее время"
              {...register('time', { required: true })}
            />
            <span>В ближайшее время</span>
          </label>
          <label className="flex items-center space-x-2 text-xl">
            <input
              type="radio"
              className='w-6 h-6'
              value="с 9:00 до 12:00"
              {...register('time', { required: true })}
            />
            <span>с 9:00 до 12:00</span>
          </label>
          <label className="flex items-center space-x-2 text-xl">
            <input
              type="radio"
              className='w-6 h-6'
              value="с 12:00 до 16:00"
              {...register('time', { required: true })}
            />
            <span>с 12:00 до 16:00</span>
          </label>
          <label className="flex items-center space-x-2 text-xl">
            <input
              type="radio"
              value="с 16:00 до 20:00"
              className='w-6 h-6'
              {...register('time', { required: true })}
            />
            <span>с 16:00 до 20:00</span>
          </label>
        </div>
        <button
          type="submit"
          className="flex items-center justify-center px-8 py-3 bg-[#FF4548] text-white rounded-full hover:bg-black hover:border-[#FF4548] border border-black transition-all duration-300 ease-in-out"
        >
          {loading ? (
            <div role="status">
              <svg
                aria-hidden="true"
                className="w-8 h-8 text-gray-200 animate-spin dark:text-gray-600 fill-black"
                viewBox="0 0 100 101"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                  fill="currentColor"
                />
                <path
                  d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                  fill="currentFill"
                />
              </svg>
              <span className="sr-only">Загрузка...</span>
            </div>
          ) : (
            'Подтвердить'
          )}
        </button>
      </>
    );
  
    return (
      <div
        id="modal-container"
        className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50 p-4"
        onClick={(e) => {
          if (e.target.id === 'modal-container') {
            onClose();
          }
        }}
      >
        <div className={`${step === 1 ? 'bg-modal' : 'bg-white'} p-6 rounded-xl shadow-lg max-w-2xl mx-auto w-full`}>
          <form className="flex flex-col space-y-6" onSubmit={handleSubmit(submit)}>
            {step === 1 ? renderStep1() : renderStep2()}
          </form>
        </div>
      </div>
    );
}